import { ColumnDef } from '@tanstack/react-table'
import { formatDateForInventoryUTC } from '@/utils/helpers'
import { Lote } from '../interfaces/inventory'
import { QuantityProgress } from '../components/QuantityProgress'
import { ExpirationDateBadge } from './components/ExpirationDateBadge'

export const lotesColumns: ColumnDef<Lote>[] = [
  {
    accessorKey: 'uniqueCode',
    header: 'Número de lote',
    cell: ({ row }) => {
      const code = row.original?.uniqueCode || '-'
      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words font-mono text-sm">{code}</span>
        </div>
      )
    },
    enableHiding: false,
  },
  {
    accessorKey: 'quantity',
    header: 'Cantidad',
    cell: ({ row }) => {
      const item = row.original
      let current = 0
      let total = 0

      // Handle both new API format and legacy format
      if (item.quantity && typeof item.quantity === 'object') {
        current = item.quantity.availableQuantity || 0
        total = item.quantity.entryQuantity || 0
      } else if (typeof item.quantity === 'number') {
        current = item.quantity
        total = item.totalQuantity || item.quantity
      }

      return (
        <div className="flex w-full flex-col items-start text-left">
          <QuantityProgress current={current} total={total} />
        </div>
      )
    },
    enableHiding: false,
    meta: {
      noWrap: true,
    },
  },
  {
    accessorKey: 'netCost',
    header: 'Costo de compra',
    cell: ({ row }) => {
      const item = row.original
      const netCost = item?.netCost

      if (!netCost || typeof netCost.amount !== 'number') {
        return (
          <div className="flex w-full flex-col items-start text-left">
            <span className="text-gray-400">-</span>
          </div>
        )
      }

      const formattedAmount = new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: netCost.codeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currencyDisplay: 'code',
      }).format(netCost.amount)

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{formattedAmount}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  /*   {
    accessorKey: 'grossCost',
    header: 'Precio de venta',
    cell: ({ row }) => {
      const item = row.original
      const grossCost = item?.grossCost

      if (!grossCost || typeof grossCost.amount !== 'number') {
        return (
          <div className="flex w-full flex-col items-start text-left">
            <span className="text-gray-400">-</span>
          </div>
        )
      }

      const formattedAmount = new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: grossCost.codeCurrency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(grossCost.amount)

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{formattedAmount}</span>
        </div>
      )
    },
    enableHiding: true,
  }, */
  {
    accessorKey: 'location',
    header: 'Ubicación',
    cell: ({ row }) => {
      const item = row.original
      let locationName = ''

      if (Array.isArray(item?.stockAreas) && item.stockAreas.length > 0) {
        const firstStockArea = item.stockAreas[0]
        if (
          'stockAreaProduct' in firstStockArea &&
          firstStockArea?.stockAreaProduct?.area?.name
        ) {
          locationName = firstStockArea.stockAreaProduct.area.name
        } else if ('name' in firstStockArea && firstStockArea.name) {
          locationName = firstStockArea.name
        }
      } else if (
        Array.isArray(item?.warehouses) &&
        item.warehouses.length > 0
      ) {
        locationName = item.warehouses[0].name
      } else if (typeof item?.location === 'string') {
        locationName = item.location // Formato legacy
      }

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{locationName || '-'}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'containers',
    header: 'Contenedor',
    cell: ({ row }) => {
      const item = row.original
      let containerName = ''

      // Prioridad 1: Contenedor del buyedReceipt (recibo de compra)
      if (item?.buyedReceipt?.name) {
        containerName = item.buyedReceipt.name
      }

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{containerName || '-'}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'suppliers',
    header: 'Proveedor',
    cell: ({ row }) => {
      const item = row.original
      let supplierName = ''

      // Solo mostrar proveedor si el lote tiene un proveedor específico
      if (item?.supplier !== null && item?.supplier !== undefined) {
        if (typeof item.supplier === 'object' && item.supplier.name) {
          supplierName = item.supplier.name
        } else if (typeof item.supplier === 'string') {
          supplierName = item.supplier
        }
      }
      // Si supplier es null, no mostrar nada (sin fallback)

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">{supplierName || '-'}</span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'entryDate',
    header: 'Fecha de entrada',
    cell: ({ row }) => {
      const item = row.original
      const entryDate = item?.entryDate

      return (
        <div className="flex w-full flex-col items-start text-left">
          <span className="break-words text-sm">
            {entryDate ? formatDateForInventoryUTC(entryDate) : '-'}
          </span>
        </div>
      )
    },
    enableHiding: true,
  },
  {
    accessorKey: 'expirationDate',
    header: 'Fecha de vencimiento',
    cell: ({ row }) => {
      const item = row.original
      let expirationDate: string | undefined

      // Support both new API format (expirations array) and legacy format (expirationDate)
      if (Array.isArray(item?.expirations) && item.expirations.length > 0) {
        // Get the earliest expiration date
        const sortedExpirations = [...item.expirations].sort(
          (a, b) => new Date(a).getTime() - new Date(b).getTime()
        )
        expirationDate = sortedExpirations[0]
      } else if (item?.expirationDate) {
        expirationDate = item.expirationDate
      }

      return <ExpirationDateBadge expirationDate={expirationDate} />
    },
    enableHiding: true,
  },
]
